import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, UserContext } from '@/App';
import { ArrowLeft, Plus, Trash2, TrendingUp, Users, Store, Package, ShieldCheck, X, Upload, Loader2, Check, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === 'admin';
  const [vendors, setVendors] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', whatsapp_number: '', description: '', image_url: '', categories: '', location: '', fssai_number: '', owner_aadhar_photo_url: '', owner_aadhar_number: '', menu_card_photos: [] });
  const [vendorFormErrors, setVendorFormErrors] = useState({});
  const [menuCardPhotoError, setMenuCardPhotoError] = useState('');
  const [menuForm, setMenuForm] = useState({ vendor_id: '', name: '', description: '', price: '', image_url: '', category: '', is_veg: true, owner_whatsapp: '' });
  const [demoMenuImage, setDemoMenuImage] = useState('');
  const [demoRestaurantActive, setDemoRestaurantActive] = useState(true);
  const [demoOrders, setDemoOrders] = useState([]);
  
  // Vendor verification states
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifiedVendorId, setVerifiedVendorId] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);
  const [verifiedWhatsappNumber, setVerifiedWhatsappNumber] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ whatsapp_number: '', aadhar_number: '' });
  const [verifyError, setVerifyError] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [menuFormErrors, setMenuFormErrors] = useState({});

  // Menu OCR states
  const [ocrImages, setOcrImages] = useState([]);
  const [ocrImagePreviews, setOcrImagePreviews] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [extractedItems, setExtractedItems] = useState([]);
  const [ocrVendorId, setOcrVendorId] = useState('');
  const [ocrError, setOcrError] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);
  const ocrInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchDemoData();
  }, []);

  const fetchData = async () => {
    try {
      const [vendorsRes, menuRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/vendors`),
        axios.get(`${API}/menu-items`),
        axios.get(`${API}/admin/analytics`)
      ]);
      setVendors(vendorsRes.data);
      setMenuItems(menuRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchDemoData = async () => {
    try {
      const [imageRes, ordersRes, restaurantRes] = await Promise.all([
        axios.get(`${API}/demo/menu-image`),
        axios.get(`${API}/demo/orders`),
        axios.get(`${API}/vendors/demo-restaurant-001`)
      ]);
      setDemoMenuImage(imageRes.data.image_url);
      setDemoOrders(ordersRes.data);
      setDemoRestaurantActive(restaurantRes.data.is_active);
    } catch (error) {
      console.error('Error fetching demo data:', error);
    }
  };

  const handleToggleDemoRestaurant = async () => {
    try {
      const res = await axios.put(`${API}/demo/restaurant/toggle`);
      setDemoRestaurantActive(res.data.is_active);
      toast.success(`Demo restaurant ${res.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to toggle demo restaurant');
    }
  };

  const handleUpdateMenuImage = async (newImageUrl) => {
    try {
      await axios.put(`${API}/demo/menu-image`, { image_url: newImageUrl });
      setDemoMenuImage(newImageUrl);
      toast.success('Menu image updated');
    } catch (error) {
      toast.error('Failed to update menu image');
    }
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    const errors = {};
    if (!vendorForm.name.trim()) errors.name = 'This field is required';
    if (!vendorForm.whatsapp_number) {
      errors.whatsapp_number = 'This field is required';
    } else {
      const cleanNumber = vendorForm.whatsapp_number.replace(/^\+91/, '').replace(/\D/g, '');
      if (cleanNumber.length !== 10) {
        errors.whatsapp_number = 'WhatsApp number must be 10 digits';
      }
    }
    if (!vendorForm.location.trim()) errors.location = 'This field is required';
    if (!vendorForm.image_url) errors.image_url = 'Restaurant image is required';
    if (!vendorForm.fssai_number) {
      errors.fssai_number = 'This field is required';
    } else if (vendorForm.fssai_number.length !== 14) {
      errors.fssai_number = 'FSSAI number must be exactly 14 digits';
    }
    if (!vendorForm.owner_aadhar_photo_url) errors.owner_aadhar_photo_url = 'This field is required';
    if (!vendorForm.owner_aadhar_number) {
      errors.owner_aadhar_number = 'This field is required';
    } else if (vendorForm.owner_aadhar_number.replace(/\s/g, '').length !== 12) {
      errors.owner_aadhar_number = 'Aadhaar number must be exactly 12 digits';
    }
    if (!vendorForm.menu_card_photos || vendorForm.menu_card_photos.length === 0) {
      errors.menu_card_photos = 'This field is required';
    }
    
    if (Object.keys(errors).length > 0) {
      setVendorFormErrors(errors);
      return;
    }
    
    setVendorFormErrors({});
    try {
      await axios.post(`${API}/vendors`, { ...vendorForm, categories: vendorForm.categories.split(',').map(c => c.trim()) });
      toast.success("Your details are under review. You'll receive a confirmation shortly.");
      setShowVendorForm(false);
      setVendorForm({ name: '', whatsapp_number: '', description: '', image_url: '', categories: '', location: '', fssai_number: '', owner_aadhar_photo_url: '', owner_aadhar_number: '', menu_card_photos: [] });
      fetchData();
    } catch (error) {
      toast.error('Failed to create vendor');
    }
  };

  const handleVendorSelect = (vendorId) => {
    // Reset verification if vendor changes
    if (vendorId !== verifiedVendorId) {
      setVerifiedVendorId(null);
      setVerificationToken(null);
      setVerifiedWhatsappNumber(null);
      setVerifyForm({ whatsapp_number: '', aadhar_number: '' });
      setVerifyError('');
    }
    
    setMenuForm({ ...menuForm, vendor_id: vendorId, owner_whatsapp: '' });
    
    // Show verification modal if vendor is selected and not yet verified
    if (vendorId && vendorId !== verifiedVendorId) {
      setShowVerifyModal(true);
    }
  };

  const handleVerifyVendor = async () => {
    if (!verifyForm.whatsapp_number || !verifyForm.aadhar_number) {
      setVerifyError('Both fields are required');
      return;
    }
    
    setVerifyLoading(true);
    setVerifyError('');
    
    try {
      const response = await axios.post(`${API}/vendors/${menuForm.vendor_id}/verify`, {
        whatsapp_number: verifyForm.whatsapp_number,
        aadhar_number: verifyForm.aadhar_number
      });
      
      if (response.data.success) {
        setVerifiedVendorId(menuForm.vendor_id);
        setVerificationToken(response.data.verification_token);
        setVerifiedWhatsappNumber(verifyForm.whatsapp_number);
        setShowVerifyModal(false);
        toast.success('Vendor verified successfully');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setVerifyError('You cannot edit other vendors.');
      } else {
        setVerifyError('Verification failed. Please try again.');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  // Normalize phone number for comparison (remove +91, spaces)
  const normalizePhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/^\+91/, '').replace(/\s/g, '').replace(/\D/g, '');
  };

  const isWhatsappMismatch = menuForm.owner_whatsapp && verifiedWhatsappNumber && 
    normalizePhone(menuForm.owner_whatsapp) !== normalizePhone(verifiedWhatsappNumber);

  const isMenuFormLocked = menuForm.vendor_id && menuForm.vendor_id !== verifiedVendorId;
  
  // Check if mandatory fields are filled (Item Name, Price, Owner WhatsApp Number)
  const isMandatoryFieldsMissing = !menuForm.name.trim() || !menuForm.price || !menuForm.owner_whatsapp;
  
  const isCreateDisabled = isMenuFormLocked || isWhatsappMismatch || isMandatoryFieldsMissing;

  const handleCreateMenuItem = async (e) => {
    e.preventDefault();
    
    // Validate mandatory fields
    const errors = {};
    if (!menuForm.name.trim()) errors.name = 'This field is required';
    if (!menuForm.price) errors.price = 'This field is required';
    if (!menuForm.owner_whatsapp) errors.owner_whatsapp = 'This field is required';
    
    if (Object.keys(errors).length > 0) {
      setMenuFormErrors(errors);
      return;
    }
    
    setMenuFormErrors({});
    
    try {
      const { owner_whatsapp, ...menuData } = menuForm;
      await axios.post(`${API}/menu-items`, { ...menuData, price: parseFloat(menuData.price), owner_whatsapp: normalizePhone(owner_whatsapp) }, {
        headers: verificationToken ? { 'X-Vendor-Verification-Token': verificationToken } : {}
      });
      toast.success('Menu item created');
      setShowMenuForm(false);
      setMenuForm({ vendor_id: '', name: '', description: '', price: '', image_url: '', category: '', is_veg: true, owner_whatsapp: '' });
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to create menu item');
      }
    }
  };

  // Menu OCR Functions
  const handleOcrImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (files.length + ocrImages.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and WEBP images are allowed');
        return;
      }
    }
    
    // Create previews and store files
    const newPreviews = [];
    const newImages = [];
    
    files.forEach(file => {
      const preview = URL.createObjectURL(file);
      newPreviews.push(preview);
      newImages.push(file);
    });
    
    setOcrImagePreviews(prev => [...prev, ...newPreviews]);
    setOcrImages(prev => [...prev, ...newImages]);
    setOcrError('');
    
    // Reset input
    if (ocrInputRef.current) {
      ocrInputRef.current.value = '';
    }
  };

  const removeOcrImage = (index) => {
    URL.revokeObjectURL(ocrImagePreviews[index]);
    setOcrImagePreviews(prev => prev.filter((_, i) => i !== index));
    setOcrImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAutoReadMenu = async () => {
    if (ocrImages.length === 0) {
      toast.error('Please upload at least one menu card image');
      return;
    }
    
    setOcrLoading(true);
    setOcrError('');
    setExtractedItems([]);
    
    try {
      // Convert images to base64
      const base64Images = await Promise.all(
        ocrImages.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );
      
      const response = await axios.post(`${API}/menu/ocr`, {
        images: base64Images
      });
      
      if (response.data.success && response.data.items.length > 0) {
        setExtractedItems(response.data.items.map((item, idx) => ({
          id: idx,
          item_name: item.item_name || '',
          price: item.price || '',
          category: item.category || 'General',
          is_veg: item.is_veg !== false,
          confidence: item.confidence || 0.5,
          raw_line: item.raw_line || ''
        })));
        toast.success(response.data.message);
      } else {
        setOcrError(response.data.message || "Couldn't read the menu card clearly. Please upload a clearer image or add items manually.");
      }
    } catch (error) {
      console.error('OCR error:', error);
      setOcrError("Couldn't read the menu card clearly. Please upload a clearer image or add items manually.");
    } finally {
      setOcrLoading(false);
    }
  };

  const updateExtractedItem = (index, field, value) => {
    setExtractedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeExtractedItem = (index) => {
    setExtractedItems(prev => prev.filter((_, i) => i !== index));
  };

  const addEmptyRow = () => {
    setExtractedItems(prev => [...prev, {
      id: Date.now(),
      item_name: '',
      price: '',
      category: 'General',
      is_veg: true,
      confidence: 1,
      raw_line: ''
    }]);
  };

  const clearAllExtracted = () => {
    setExtractedItems([]);
    // Also clear images
    ocrImagePreviews.forEach(url => URL.revokeObjectURL(url));
    setOcrImagePreviews([]);
    setOcrImages([]);
    setOcrError('');
  };

  const handlePublishItems = async () => {
    if (!ocrVendorId) {
      toast.error('Please select a vendor');
      return;
    }
    
    // Filter valid items (has name and valid price)
    const validItems = extractedItems.filter(item => {
      if (!item.item_name || !String(item.item_name).trim()) return false;
      if (item.price === '' || item.price === null || item.price === undefined) return false;
      const price = parseFloat(item.price);
      return !isNaN(price) && price > 0;
    });
    
    if (validItems.length === 0) {
      toast.error('No valid items to publish. Each item needs a name and valid price.');
      return;
    }
    
    setPublishLoading(true);
    try {
      const response = await axios.post(`${API}/menu/bulk-create`, {
        vendor_id: ocrVendorId,
        items: validItems
      });
      
      if (response.data.success) {
        toast.success(`Created ${response.data.created_count} menu items successfully!`);
        clearAllExtracted();
        fetchData(); // Refresh menu items list
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish menu items');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!ocrVendorId) {
      toast.error('Please select a vendor');
      return;
    }
    
    try {
      await axios.post(`${API}/menu/bulk-draft-save`, {
        vendor_id: ocrVendorId,
        items: extractedItems
      });
      toast.success('Draft saved successfully');
    } catch (error) {
      toast.error('Failed to save draft');
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) return { label: 'High', class: 'bg-green-100 text-green-800' };
    if (confidence >= 0.5) return { label: 'Medium', class: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Low', class: 'bg-red-100 text-red-800' };
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full"><ArrowLeft className="w-6 h-6" /></button>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          </div>
          <p className="text-white/90 ml-14">Manage vendors, menu items, and view analytics</p>
        </div>
      </div>

      {analytics && isAdmin && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-md p-6"><div className="flex items-center gap-3 mb-2"><Package className="w-8 h-8 text-blue-600" /><h3 className="font-semibold">Orders</h3></div><p className="text-3xl font-bold">{analytics.total_orders}</p></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="flex items-center gap-3 mb-2"><TrendingUp className="w-8 h-8 text-green-600" /><h3 className="font-semibold">Revenue</h3></div><p className="text-3xl font-bold">₹{analytics.total_revenue.toFixed(0)}</p></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="flex items-center gap-3 mb-2"><Users className="w-8 h-8 text-purple-600" /><h3 className="font-semibold">Users</h3></div><p className="text-3xl font-bold">{analytics.total_users}</p></div>
            <div className="bg-white rounded-xl shadow-md p-6"><div className="flex items-center gap-3 mb-2"><Store className="w-8 h-8 text-orange-600" /><h3 className="font-semibold">Vendors</h3></div><p className="text-3xl font-bold">{analytics.total_vendors}</p></div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4">
        <Tabs defaultValue="vendors">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="vendors">Vendors</TabsTrigger>
            <TabsTrigger value="menu">Menu Items</TabsTrigger>
            <TabsTrigger value="demo">Demo Restaurant</TabsTrigger>
          </TabsList>
          <TabsContent value="vendors">
            <div className="mb-4"><Button onClick={() => setShowVendorForm(!showVendorForm)} className="btn-primary text-white px-6 py-3 rounded-lg flex items-center gap-2"><Plus className="w-5 h-5" />Add Vendor</Button></div>
            {showVendorForm && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-xl font-bold mb-4">Create New Vendor</h3>
                <form onSubmit={handleCreateVendor} className="space-y-4">
                  {/* Vendor Name - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name <span className="text-red-500">*</span></label>
                    <Input 
                      placeholder="Vendor Name" 
                      value={vendorForm.name} 
                      onChange={(e) => { setVendorForm({ ...vendorForm, name: e.target.value }); setVendorFormErrors({ ...vendorFormErrors, name: '' }); }}
                      className={vendorFormErrors.name ? 'border-red-500' : ''}
                    />
                    {vendorFormErrors.name && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.name}</p>}
                  </div>
                  
                  {/* WhatsApp Number - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number <span className="text-red-500">*</span></label>
                    <Input 
                      type="tel"
                      placeholder="Enter WhatsApp number (10 digits)" 
                      value={vendorForm.whatsapp_number} 
                      onChange={(e) => { 
                        let val = e.target.value;
                        if (!val.startsWith('+91') && val.length > 0 && /^\d/.test(val)) {
                          val = val.replace(/\D/g, '').slice(0, 10);
                        } else if (val.startsWith('+91')) {
                          val = '+91' + val.slice(3).replace(/\D/g, '').slice(0, 10);
                        }
                        setVendorForm({ ...vendorForm, whatsapp_number: val }); 
                        setVendorFormErrors({ ...vendorFormErrors, whatsapp_number: '' }); 
                      }}
                      className={vendorFormErrors.whatsapp_number ? 'border-red-500' : ''}
                    />
                    {vendorFormErrors.whatsapp_number && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.whatsapp_number}</p>}
                  </div>
                  
                  <Textarea placeholder="Description" value={vendorForm.description} onChange={(e) => setVendorForm({ ...vendorForm, description: e.target.value })} />
                  {/* Restaurant Image - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Image <span className="text-red-500">*</span></label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setVendorForm({ ...vendorForm, image_url: reader.result });
                            setVendorFormErrors({ ...vendorFormErrors, image_url: '' });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className={`w-full p-2 border rounded-lg text-sm ${vendorFormErrors.image_url ? 'border-red-500' : ''}`}
                      data-testid="restaurant-image-upload"
                    />
                    {vendorFormErrors.image_url && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.image_url}</p>}
                    {vendorForm.image_url && (
                      <div className="mt-2 relative inline-block">
                        <img src={vendorForm.image_url} alt="Restaurant preview" className="w-32 h-20 object-cover rounded-lg border" />
                        <button
                          type="button"
                          onClick={() => setVendorForm({ ...vendorForm, image_url: '' })}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                  <Input placeholder="Categories (comma separated)" value={vendorForm.categories} onChange={(e) => setVendorForm({ ...vendorForm, categories: e.target.value })} />
                  
                  {/* Location - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location <span className="text-red-500">*</span></label>
                    <Input 
                      placeholder="Location" 
                      value={vendorForm.location} 
                      onChange={(e) => { setVendorForm({ ...vendorForm, location: e.target.value }); setVendorFormErrors({ ...vendorFormErrors, location: '' }); }}
                      className={vendorFormErrors.location ? 'border-red-500' : ''}
                    />
                    {vendorFormErrors.location && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.location}</p>}
                  </div>
                  
                  {/* FSSAI Number - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FSSAI Number <span className="text-red-500">*</span></label>
                    <Input 
                      placeholder="Enter 14-digit FSSAI number" 
                      value={vendorForm.fssai_number} 
                      onChange={(e) => { setVendorForm({ ...vendorForm, fssai_number: e.target.value.replace(/\D/g, '').slice(0, 14) }); setVendorFormErrors({ ...vendorFormErrors, fssai_number: '' }); }}
                      maxLength={14}
                      className={vendorFormErrors.fssai_number ? 'border-red-500' : ''}
                    />
                    {vendorFormErrors.fssai_number && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.fssai_number}</p>}
                  </div>
                  
                  {/* Aadhaar Card Photo - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Card Photo (Restaurant Owner) <span className="text-red-500">*</span></label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setVendorForm({ ...vendorForm, owner_aadhar_photo_url: reader.result });
                            setVendorFormErrors({ ...vendorFormErrors, owner_aadhar_photo_url: '' });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className={`w-full p-2 border rounded-lg text-sm ${vendorFormErrors.owner_aadhar_photo_url ? 'border-red-500' : ''}`}
                    />
                    {vendorFormErrors.owner_aadhar_photo_url && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.owner_aadhar_photo_url}</p>}
                    {vendorForm.owner_aadhar_photo_url && (
                      <div className="mt-2 relative inline-block">
                        <img src={vendorForm.owner_aadhar_photo_url} alt="Aadhaar preview" className="w-32 h-20 object-cover rounded-lg border" />
                        <button
                          type="button"
                          onClick={() => setVendorForm({ ...vendorForm, owner_aadhar_photo_url: '' })}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Menu Card Photo - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Menu Card Photo <span className="text-red-500">*</span></label>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                        const maxFileSize = 100 * 1024 * 1024; // 100 MB
                        const maxFiles = 20;
                        
                        // Check file count
                        const totalFiles = vendorForm.menu_card_photos.length + files.length;
                        if (totalFiles > maxFiles) {
                          setMenuCardPhotoError('You can upload a maximum of 20 menu card photos.');
                          e.target.value = '';
                          return;
                        }
                        
                        // Validate each file
                        const validFiles = [];
                        for (const file of files) {
                          if (!allowedTypes.includes(file.type)) {
                            setMenuCardPhotoError('Only image files (jpg, jpeg, png, webp) are allowed.');
                            e.target.value = '';
                            return;
                          }
                          if (file.size > maxFileSize) {
                            setMenuCardPhotoError('Each menu card photo must be less than 100 MB.');
                            e.target.value = '';
                            return;
                          }
                          validFiles.push(file);
                        }
                        
                        // Convert to base64 and store
                        setMenuCardPhotoError('');
                        setVendorFormErrors({ ...vendorFormErrors, menu_card_photos: '' });
                        
                        const readFiles = validFiles.map(file => {
                          return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(file);
                          });
                        });
                        
                        Promise.all(readFiles).then(results => {
                          setVendorForm(prev => ({ 
                            ...prev, 
                            menu_card_photos: [...prev.menu_card_photos, ...results]
                          }));
                        });
                        
                        e.target.value = '';
                      }}
                      className={`w-full p-2 border rounded-lg text-sm ${vendorFormErrors.menu_card_photos || menuCardPhotoError ? 'border-red-500' : ''}`}
                    />
                    {vendorForm.menu_card_photos.length > 0 && (
                      <p className="text-gray-600 text-xs mt-1">{vendorForm.menu_card_photos.length} file{vendorForm.menu_card_photos.length > 1 ? 's' : ''} selected</p>
                    )}
                    {menuCardPhotoError && <p className="text-red-500 text-xs mt-1">{menuCardPhotoError}</p>}
                    {vendorFormErrors.menu_card_photos && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.menu_card_photos}</p>}
                    {vendorForm.menu_card_photos.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {vendorForm.menu_card_photos.map((photo, index) => (
                          <div key={index} className="relative inline-block">
                            <img src={photo} alt={`Menu card ${index + 1}`} className="w-20 h-20 object-cover rounded-lg border" />
                            <button
                              type="button"
                              onClick={() => {
                                setVendorForm(prev => ({
                                  ...prev,
                                  menu_card_photos: prev.menu_card_photos.filter((_, i) => i !== index)
                                }));
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Aadhaar Card Number - Required */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Card Number <span className="text-red-500">*</span></label>
                    <Input 
                      placeholder="Enter 12-digit Aadhaar number" 
                      value={vendorForm.owner_aadhar_number} 
                      onChange={(e) => { setVendorForm({ ...vendorForm, owner_aadhar_number: e.target.value.replace(/\D/g, '').slice(0, 12) }); setVendorFormErrors({ ...vendorFormErrors, owner_aadhar_number: '' }); }}
                      maxLength={12}
                      className={vendorFormErrors.owner_aadhar_number ? 'border-red-500' : ''}
                    />
                    {vendorFormErrors.owner_aadhar_number && <p className="text-red-500 text-xs mt-1">{vendorFormErrors.owner_aadhar_number}</p>}
                  </div>
                  
                  <div className="flex gap-3"><Button type="submit" className="btn-primary text-white">Submit</Button><Button type="button" onClick={() => setShowVendorForm(false)} className="bg-gray-200">Cancel</Button></div>
                  <p className="text-gray-500 text-xs mt-2">Having trouble submitting? Contact us on WhatsApp: 7620715987</p>
                </form>
              </div>
            )}
            <div className="space-y-4">
              {vendors.map(vendor => (
                <div key={vendor.id} className="bg-white rounded-xl shadow-md p-6 flex items-start gap-4">
                  <img src={vendor.image_url} alt={vendor.name} className="w-24 h-24 rounded-lg object-cover" />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg">{vendor.name}</h3>
                    <p className="text-sm text-gray-600">{vendor.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-sm">
                      {vendor.whatsapp_number && <span className="text-gray-500">📱 {vendor.whatsapp_number}</span>}
                      {vendor.location && <span className="text-gray-500">📍 {vendor.location}</span>}
                    </div>
                    <div className="flex gap-1 mt-2">{vendor.categories.map((cat, idx) => <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">{cat}</span>)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${vendor.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {vendor.status === 'APPROVED' ? 'Approved' : 'Under Review'}
                    </span>
                    {isAdmin && vendor.status !== 'APPROVED' && (
                      <button 
                        onClick={async () => { 
                          await axios.post(`${API}/vendors/${vendor.id}/approve`, {}, { withCredentials: true }); 
                          toast.success('Vendor approved'); 
                          fetchData(); 
                        }} 
                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg"
                      >
                        Approve
                      </button>
                    )}
                    {isAdmin && <button onClick={async () => { await axios.delete(`${API}/vendors/${vendor.id}`, { withCredentials: true }); toast.success('Vendor deleted'); fetchData(); }} className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"><Trash2 className="w-5 h-5" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="menu">
            {/* Check if any vendor is not approved - show warning */}
            {vendors.some(v => v.status !== 'APPROVED' && !v.is_demo) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 text-yellow-800 text-sm">
                ⚠️ Some vendors are under review. Menu items can only be added for approved vendors.
              </div>
            )}
            
            {/* Menu OCR Section - Add Menu from Menu Card */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6" data-testid="menu-ocr-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Add Menu from Menu Card (Fast)</h3>
                  <p className="text-sm text-gray-500">Upload menu card images to auto-extract items</p>
                </div>
              </div>
              
              {/* Vendor Selection for OCR */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Vendor</label>
                <select 
                  value={ocrVendorId} 
                  onChange={(e) => setOcrVendorId(e.target.value)} 
                  className="w-full p-2 border rounded-lg"
                  data-testid="ocr-vendor-select"
                >
                  <option value="">Select Vendor</option>
                  {vendors.filter(v => v.status === 'APPROVED' || v.is_demo).map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Image Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Menu Card Images (max 5)
                </label>
                <input
                  ref={ocrInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleOcrImageSelect}
                  className="hidden"
                  data-testid="ocr-image-input"
                />
                <div className="flex flex-wrap gap-3" data-testid="ocr-image-previews">
                  {ocrImagePreviews.map((preview, idx) => (
                    <div key={idx} className="relative w-24 h-24" data-testid={`ocr-image-preview-${idx}`}>
                      <img src={preview} alt={`Menu ${idx+1}`} className="w-full h-full object-cover rounded-lg border" />
                      <button
                        type="button"
                        onClick={() => removeOcrImage(idx)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        data-testid={`ocr-remove-image-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {ocrImages.length < 5 && (
                    <button
                      type="button"
                      onClick={() => ocrInputRef.current?.click()}
                      className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-green-500 hover:text-green-500 transition-colors"
                      data-testid="ocr-add-image-btn"
                    >
                      <Upload className="w-6 h-6" />
                      <span className="text-xs">Add</span>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Auto Read Button */}
              <Button
                type="button"
                onClick={handleAutoReadMenu}
                disabled={ocrLoading || ocrImages.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center gap-2"
                data-testid="ocr-auto-read-btn"
              >
                {ocrLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reading Menu...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Auto Read Menu
                  </>
                )}
              </Button>
              
              {/* Error Message */}
              {ocrError && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{ocrError}</p>
                </div>
              )}
              
              {/* Extracted Items Table */}
              {extractedItems.length > 0 && (
                <div className="mt-6" data-testid="ocr-extracted-items-section">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-800" data-testid="ocr-extracted-count">Extracted Items ({extractedItems.length})</h4>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={addEmptyRow} data-testid="ocr-add-row-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Row
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={clearAllExtracted} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="ocr-clear-all-btn">
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] border rounded-lg" data-testid="ocr-items-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Item Name</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-24">Price (₹)</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-28">Veg/Non-veg</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-32">Category</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-20">Confidence</th>
                          <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {extractedItems.map((item, idx) => {
                          const badge = getConfidenceBadge(item.confidence);
                          const priceError = item.price !== '' && (isNaN(parseFloat(item.price)) || parseFloat(item.price) < 0);
                          return (
                            <tr key={item.id} className="hover:bg-gray-50" data-testid={`ocr-item-row-${idx}`}>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.item_name}
                                  onChange={(e) => updateExtractedItem(idx, 'item_name', e.target.value)}
                                  placeholder="Item name"
                                  className="h-8 text-sm"
                                  data-testid={`ocr-item-name-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  value={item.price}
                                  onChange={(e) => updateExtractedItem(idx, 'price', e.target.value)}
                                  placeholder="0"
                                  className={`h-8 text-sm ${priceError ? 'border-red-500' : ''}`}
                                  data-testid={`ocr-item-price-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={item.is_veg ? 'veg' : 'nonveg'}
                                  onChange={(e) => updateExtractedItem(idx, 'is_veg', e.target.value === 'veg')}
                                  className="w-full h-8 text-sm border rounded px-2"
                                  data-testid={`ocr-item-veg-${idx}`}
                                >
                                  <option value="veg">Veg</option>
                                  <option value="nonveg">Non-veg</option>
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  value={item.category}
                                  onChange={(e) => updateExtractedItem(idx, 'category', e.target.value)}
                                  placeholder="Category"
                                  className="h-8 text-sm"
                                  data-testid={`ocr-item-category-${idx}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.class}`} data-testid={`ocr-item-confidence-${idx}`}>
                                  {badge.label}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeExtractedItem(idx)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  data-testid={`ocr-delete-item-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="mt-4 flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSaveDraft}
                      disabled={!ocrVendorId}
                      data-testid="ocr-save-draft-btn"
                    >
                      Save Draft
                    </Button>
                    <Button
                      type="button"
                      onClick={handlePublishItems}
                      disabled={publishLoading || !ocrVendorId || extractedItems.length === 0}
                      className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                      data-testid="ocr-publish-btn"
                    >
                      {publishLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Confirm & Publish
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-4"><Button onClick={() => setShowMenuForm(!showMenuForm)} className="btn-primary text-white px-6 py-3 rounded-lg flex items-center gap-2"><Plus className="w-5 h-5" />Add Menu Item</Button></div>
            {showMenuForm && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-xl font-bold mb-4">Create Menu Item</h3>
                <form onSubmit={handleCreateMenuItem} className="space-y-4">
                  <select value={menuForm.vendor_id} onChange={(e) => handleVendorSelect(e.target.value)} className="w-full p-2 border rounded-lg" data-testid="vendor-select">
                    <option value="">Select Vendor</option>
                    {vendors.filter(v => v.status === 'APPROVED' || v.is_demo).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  {menuForm.vendor_id && verifiedVendorId === menuForm.vendor_id && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <ShieldCheck className="w-4 h-4" />
                      <span>Vendor verified</span>
                    </div>
                  )}
                  {menuForm.vendor_id && vendors.find(v => v.id === menuForm.vendor_id)?.status !== 'APPROVED' && !vendors.find(v => v.id === menuForm.vendor_id)?.is_demo && (
                    <p className="text-yellow-600 text-xs">This vendor is under review. Menu items cannot be added yet.</p>
                  )}
                  
                  {/* Item Name - Mandatory */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item Name <span className="text-red-500">*</span></label>
                    <Input 
                      placeholder="Item Name" 
                      value={menuForm.name} 
                      onChange={(e) => { setMenuForm({ ...menuForm, name: e.target.value }); setMenuFormErrors({ ...menuFormErrors, name: '' }); }}
                      disabled={isMenuFormLocked} 
                      className={`${isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${menuFormErrors.name ? 'border-red-500' : ''}`} 
                      data-testid="menu-item-name" 
                    />
                    {menuFormErrors.name && <p className="text-red-500 text-xs mt-1">{menuFormErrors.name}</p>}
                  </div>
                  
                  {/* Description - Not mandatory */}
                  <Textarea placeholder="Description" value={menuForm.description} onChange={(e) => setMenuForm({ ...menuForm, description: e.target.value })} disabled={isMenuFormLocked} className={isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} data-testid="menu-item-description" />
                  
                  {/* Price - Mandatory */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price <span className="text-red-500">*</span></label>
                    <Input 
                      type="number" 
                      placeholder="Price" 
                      value={menuForm.price} 
                      onChange={(e) => { setMenuForm({ ...menuForm, price: e.target.value }); setMenuFormErrors({ ...menuFormErrors, price: '' }); }}
                      disabled={isMenuFormLocked} 
                      className={`${isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${menuFormErrors.price ? 'border-red-500' : ''}`} 
                      data-testid="menu-item-price" 
                    />
                    {menuFormErrors.price && <p className="text-red-500 text-xs mt-1">{menuFormErrors.price}</p>}
                    <p className="text-gray-500 text-xs mt-1">Price must be same as menu card or less than menu card . If not, item will get deleted.</p>
                  </div>
                  
                  {/* Image URL - Not mandatory */}
                  <Input placeholder="Image URL" value={menuForm.image_url} onChange={(e) => setMenuForm({ ...menuForm, image_url: e.target.value })} disabled={isMenuFormLocked} className={isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} data-testid="menu-item-image" />
                  
                  {/* Category - Not mandatory */}
                  <Input placeholder="Category" value={menuForm.category} onChange={(e) => setMenuForm({ ...menuForm, category: e.target.value })} disabled={isMenuFormLocked} className={isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} data-testid="menu-item-category" />
                  
                  {/* Owner WhatsApp Number - Mandatory */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner WhatsApp Number <span className="text-red-500">*</span></label>
                    <Input 
                      type="tel"
                      placeholder="Enter the same number used for verification" 
                      value={menuForm.owner_whatsapp} 
                      onChange={(e) => { setMenuForm({ ...menuForm, owner_whatsapp: e.target.value.replace(/[^\d+]/g, '') }); setMenuFormErrors({ ...menuFormErrors, owner_whatsapp: '' }); }}
                      disabled={isMenuFormLocked}
                      className={`w-full ${isMenuFormLocked ? 'bg-gray-100 cursor-not-allowed' : ''} ${isWhatsappMismatch || menuFormErrors.owner_whatsapp ? 'border-red-500' : ''}`}
                      data-testid="menu-item-owner-whatsapp"
                    />
                    {menuFormErrors.owner_whatsapp && <p className="text-red-500 text-xs mt-1">{menuFormErrors.owner_whatsapp}</p>}
                    {isWhatsappMismatch && (
                      <p className="text-red-500 text-xs mt-1" data-testid="whatsapp-mismatch-error">Number mismatch. You cannot add items for other vendors.</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2"><input type="checkbox" id="is_veg" checked={menuForm.is_veg} onChange={(e) => setMenuForm({ ...menuForm, is_veg: e.target.checked })} disabled={isMenuFormLocked} /><label htmlFor="is_veg" className={isMenuFormLocked ? 'text-gray-400' : ''}>Vegetarian</label></div>
                  <div className="flex gap-3"><Button type="submit" className="btn-primary text-white" disabled={isCreateDisabled} data-testid="create-menu-item-btn">Create Item</Button><Button type="button" onClick={() => setShowMenuForm(false)} className="bg-gray-200">Cancel</Button></div>
                </form>
              </div>
            )}
            <div className="space-y-4">
              {menuItems.map(item => {
                const itemVendor = vendors.find(v => v.id === item.vendor_id);
                const isVendorApproved = itemVendor?.status === 'APPROVED' || itemVendor?.is_demo;
                return (
                <div key={item.id} className="bg-white rounded-xl shadow-md p-6 flex items-start gap-4">
                  <img src={item.image_url} alt={item.name} className="w-24 h-24 rounded-lg object-cover" />
                  <div className="flex-1"><h3 className="font-bold text-lg">{item.name}</h3><p className="text-sm text-gray-600">{item.description}</p><div className="flex items-center gap-4 mt-2"><span className="font-bold text-blue-600">₹{item.price}</span><span className="text-sm text-gray-500">{item.category}</span></div></div>
                  {isAdmin && isVendorApproved && <button onClick={async () => { await axios.delete(`${API}/menu-items/${item.id}`, { withCredentials: true }); toast.success('Item deleted'); fetchData(); }} className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg"><Trash2 className="w-5 h-5" /></button>}
                </div>
              );})}
            </div>
          </TabsContent>

          {/* Demo Restaurant Tab */}
          <TabsContent value="demo">
            <div className="space-y-6">
              {/* Restaurant Status */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Demo Restaurant Status</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">FoodMatrix Demo Restaurant</p>
                    <p className="text-sm text-gray-600">For investor presentations</p>
                  </div>
                  <Button
                    onClick={handleToggleDemoRestaurant}
                    className={`${demoRestaurantActive ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'} text-white px-6 py-2 rounded-lg`}
                    data-testid="toggle-demo-restaurant"
                  >
                    {demoRestaurantActive ? 'Active' : 'Inactive'}
                  </Button>
                </div>
              </div>

              {/* Menu Image Management */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Menu Image</h3>
                {demoMenuImage && (
                  <div className="mb-4">
                    <img src={demoMenuImage} alt="Current Menu" className="w-full max-w-md rounded-lg border-2 border-gray-200" />
                  </div>
                )}
                <div className="space-y-3">
                  <Input
                    placeholder="New menu image URL"
                    id="new-menu-image"
                    data-testid="menu-image-input"
                  />
                  <Button
                    onClick={() => {
                      const newUrl = document.getElementById('new-menu-image').value;
                      if (newUrl) handleUpdateMenuImage(newUrl);
                    }}
                    className="btn-primary text-white"
                    data-testid="update-menu-image-button"
                  >
                    Update Menu Image
                  </Button>
                </div>
              </div>

              {/* Demo Orders */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Demo Orders ({demoOrders.length})</h3>
                {demoOrders.length === 0 ? (
                  <p className="text-gray-500">No demo orders yet</p>
                ) : (
                  <div className="space-y-3">
                    {demoOrders.slice(0, 10).map((order) => (
                      <div key={order.id} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold">Order #{order.id.slice(0, 8)}</p>
                            <p className="text-sm text-gray-600">{order.items.length} items</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">₹{order.total_amount}</p>
                            <p className="text-xs text-gray-500">{order.status}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Menu Items for Demo Restaurant */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">Demo Menu Items</h3>
                <div className="space-y-3">
                  {menuItems.filter(item => item.vendor_id === 'demo-restaurant-001').map((item) => (
                    <div key={item.id} className="flex items-center justify-between border rounded-lg p-4">
                      <div>
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">₹{item.price}</p>
                        <Input
                          type="number"
                          defaultValue={item.price}
                          className="w-24 mt-2"
                          onBlur={async (e) => {
                            const newPrice = parseFloat(e.target.value);
                            if (newPrice && newPrice !== item.price) {
                              try {
                                await axios.put(`${API}/menu-items/${item.id}`, { price: newPrice });
                                toast.success('Price updated');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to update price');
                              }
                            }
                          }}
                          data-testid={`price-input-${item.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Vendor Verification Modal */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="verify-modal">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold">Verify Vendor</h3>
              </div>
              <button 
                onClick={() => {
                  setShowVerifyModal(false);
                  setMenuForm({ ...menuForm, vendor_id: '' });
                  setVerifyError('');
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
                data-testid="close-verify-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered Mobile / WhatsApp Number <span className="text-red-500">*</span></label>
                <Input 
                  placeholder="Enter WhatsApp number"
                  value={verifyForm.whatsapp_number}
                  onChange={(e) => setVerifyForm({ ...verifyForm, whatsapp_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  maxLength={10}
                  data-testid="verify-whatsapp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhaar Card Number <span className="text-red-500">*</span></label>
                <Input 
                  placeholder="Enter 12-digit Aadhaar"
                  value={verifyForm.aadhar_number}
                  onChange={(e) => setVerifyForm({ ...verifyForm, aadhar_number: e.target.value.replace(/\D/g, '').slice(0, 12) })}
                  maxLength={12}
                  data-testid="verify-aadhar"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mb-4">Enter the same details used during vendor registration.</p>
            
            {verifyError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm" data-testid="verify-error">
                {verifyError}
              </div>
            )}
            
            <div className="flex gap-3">
              <Button 
                onClick={handleVerifyVendor} 
                className="btn-primary text-white flex-1"
                disabled={verifyLoading}
                data-testid="verify-submit-btn"
              >
                {verifyLoading ? 'Verifying...' : 'Verify'}
              </Button>
              <Button 
                type="button" 
                onClick={() => {
                  setShowVerifyModal(false);
                  setMenuForm({ ...menuForm, vendor_id: '' });
                  setVerifyError('');
                }}
                className="bg-gray-200"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
